/**
 * m3 echo Frontend - Cheatsheet Tab
 *
 * Admin tab for editing the optimization cheatsheet used by Layer 5.
 * Supports versioning with archive and restore functionality.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import { settingsApi } from '../../services/api';

interface CheatsheetVersion {
  id: number;
  content: string;
  created_by: string | null;
  created_at: string;
}

interface CheatsheetData {
  current: CheatsheetVersion | null;
  archive: CheatsheetVersion[];
}

export const CheatsheetTab: React.FC = () => {
  const [data, setData] = useState<CheatsheetData | null>(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadCheatsheet();
  }, []);

  const loadCheatsheet = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getCheatsheet();
      setData(response.data);
      setEditContent(response.data.current?.content || '');
    } catch (err: any) {
      console.error('Fehler beim Laden des Cheatsheets:', err);
      setError(err.message || 'Fehler beim Laden des Cheatsheets');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await settingsApi.updateCheatsheet({ content: editContent });
      setData(response.data);
      setEditContent(response.data.current?.content || '');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = (content: string) => {
    setEditContent(content);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const CHEATSHEET_MAX_CHARS = 20000;
  const hasChanges = data?.current?.content !== editContent;
  const isOverLimit = editContent.length > CHEATSHEET_MAX_CHARS;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <DescriptionIcon sx={{ mr: 1, fontSize: 28 }} />
        <Typography variant="h6">
          Cheatsheet
        </Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Das Cheatsheet definiert die Bewertungskriterien, die Layer 5 (Optimierungs-Analyse) verwendet.
        Jede Speicherung erstellt eine neue Version. Alte Versionen bleiben im Archiv erhalten.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cheatsheet erfolgreich gespeichert!
        </Alert>
      )}

      {!loading && data && (
        <>
          {/* Editor */}
          <TextField
            fullWidth
            multiline
            minRows={16}
            maxRows={30}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="Cheatsheet-Inhalt..."
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
            sx={{ mb: 1 }}
          />

          <Typography
            variant="body2"
            sx={{ mb: 2, fontFamily: 'monospace' }}
            color={isOverLimit ? 'error' : 'textSecondary'}
          >
            {editContent.length.toLocaleString('de-DE')} / {CHEATSHEET_MAX_CHARS.toLocaleString('de-DE')} Zeichen
            {isOverLimit && ` (${(editContent.length - CHEATSHEET_MAX_CHARS).toLocaleString('de-DE')} zu viel)`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasChanges || isOverLimit}
            >
              {saving ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Speichern...
                </>
              ) : (
                'Speichern'
              )}
            </Button>
            {hasChanges && (
              <Typography variant="body2" color="textSecondary">
                Ungespeicherte Änderungen
              </Typography>
            )}
          </Box>

          {/* Archive */}
          {data.archive.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Archiv
              </Typography>

              {data.archive.map((version) => (
                <Accordion key={version.id} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(version.created_at)}
                      {version.created_by && (
                        <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                          von {version.created_by}
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      maxRows={20}
                      value={version.content}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={() => handleRestore(version.content)}
                    >
                      Wiederherstellen
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default CheatsheetTab;
